import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { INCIDENT_EVENT_LABELS, INCIDENT_STATUS_LABELS } from '@wodi/domain';
import type { IncidentView } from '@wodi/shared';
import { LoadingBlock, Notice, PageHeader, Section } from '@wodi/ui';

import { incidentQuery } from '../api/incidents.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { dateTimeText, LoadFailed, NotAllowed } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { ActionsList, ReviewPanel } from './review-panel.tsx';
import { IncidentStatusTag } from './words.tsx';

// One incident (docs/plans/phase3-operations-modules.md §1.4): the report, the patient as
// this reader may see them, the review and the history. Opening it is audited on the server;
// a shown patient is also audited as a view of their record.

const route = getRouteApi('/_app/incidents/$incidentId');

function PatientLine({ incident }: { incident: IncidentView }) {
  const p = incident.patient;
  if (p.state === 'none') return <>None linked</>;
  if (p.state === 'hidden') return <>Linked to a patient record you can&apos;t open</>;
  return (
    <>
      <Link to="/patients/$patientId" params={{ patientId: p.patient.id }}>
        {p.patient.displayName}
      </Link>{' '}
      <span className="wodi-id">{p.patient.patientNumber}</span>
      {p.visit === null ? null : (
        <>
          , visit <span className="wodi-id">{p.visit.visitNumber}</span>
        </>
      )}
    </>
  );
}

function Report({ incident }: { incident: IncidentView }) {
  return (
    <Section title="Report">
      <dl className={styles.facts}>
        <dt>Status</dt>
        <dd>
          <IncidentStatusTag status={incident.status} />
          {incident.statusReason === null ? null : (
            <span className={styles.meta}> {incident.statusReason}</span>
          )}
        </dd>
        <dt>Category</dt>
        <dd>{incident.category.display}</dd>
        <dt>Harm</dt>
        <dd>{incident.severity.display}</dd>
        <dt>Happened</dt>
        <dd>{dateTimeText(incident.occurredAt)}</dd>
        <dt>Reported</dt>
        <dd>{dateTimeText(incident.reportedAt)}</dd>
        <dt>Where</dt>
        <dd>{incident.location}</dd>
        <dt>Reported by</dt>
        <dd>
          {incident.reporter === null ? 'Anonymous' : incident.reporter.displayName}
          {incident.anonymous && incident.reporter !== null ? ' (hidden from colleagues)' : ''}
        </dd>
        {incident.source === 'patient_feedback' ? (
          <>
            <dt>Came from</dt>
            <dd>Patient feedback</dd>
          </>
        ) : null}
        <dt>Patient</dt>
        <dd>
          <PatientLine incident={incident} />
        </dd>
        <dt>Equipment</dt>
        <dd>{incident.equipmentText ?? 'None recorded'}</dd>
        <dt>Reviewer</dt>
        <dd>{incident.reviewer?.displayName ?? 'Not yet assigned'}</dd>
      </dl>
      <h3>What happened</h3>
      <p className={styles.prose}>{incident.description}</p>
      <h3>What was done straight away</h3>
      <p className={styles.prose}>{incident.immediateAction ?? 'Nothing recorded.'}</p>
      {incident.rootCause === null ? null : (
        <>
          <h3>Root cause</h3>
          <p className={styles.prose}>{incident.rootCause}</p>
        </>
      )}
    </Section>
  );
}

function History({ incident }: { incident: IncidentView }) {
  return (
    <Section title="History" description="Every step, the oldest first.">
      <ol className={styles.list} aria-label="History">
        {incident.events.map((e) => (
          <li key={e.id} className={styles.item}>
            <div className={styles.itemMain}>
              <span>
                {INCIDENT_EVENT_LABELS[e.kind]}
                {e.toStatus !== null && e.kind !== 'reported'
                  ? `: now ${INCIDENT_STATUS_LABELS[e.toStatus].toLowerCase()}`
                  : ''}
              </span>
              {e.note === null ? null : <span className={styles.prose}>{e.note}</span>}
            </div>
            <span className={styles.meta}>
              {dateTimeText(e.at)}, {e.by.displayName}
            </span>
          </li>
        ))}
      </ol>
    </Section>
  );
}

export function IncidentPage() {
  const { incidentId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const allowed = session !== null && can(session, 'incidents.report');
  const incident = useQuery({ ...incidentQuery(incidentId), enabled: allowed });
  if (!allowed) return <NotAllowed title="Incident" what="see incidents" />;
  return (
    <div className={styles.page}>
      <PageHeader
        title={
          incident.data === undefined ? 'Incident' : `Incident ${incident.data.incidentNumber}`
        }
      />
      <p className={styles.meta}>
        <Link to="/incidents">Incidents</Link>
      </p>
      {online ? null : (
        <Notice tone="info">
          You&apos;re offline. This shows what was last loaded; review steps work again when
          you&apos;re back online.
        </Notice>
      )}
      {incident.isPending ? <LoadingBlock label="Loading the incident" rows={8} /> : null}
      {incident.isError ? (
        <LoadFailed
          error={incident.error}
          fallback="The incident couldn't be loaded. Try again."
          onRetry={() => void incident.refetch()}
        />
      ) : null}
      {incident.isSuccess ? (
        <>
          <Report incident={incident.data} />
          {incident.data.mayManage ? (
            <ReviewPanel incident={incident.data} online={online} />
          ) : (
            <Section title="Actions" description="What will be done, by whom and by when.">
              <ActionsList incident={incident.data} online={online} />
            </Section>
          )}
          <History incident={incident.data} />
        </>
      ) : null}
    </div>
  );
}
